import { createBaseSocket } from '@shared/api/baseSocket'
import { withSocketTicket } from '@shared/api'

type CandleListener = (msg: WsCandleUpdateMessage) => void

const candleListeners = new Set<CandleListener>()
const candleKey = (symbol: string, resolution: string) => `${symbol}|${resolution}`

const createTickerSocket = () => {
  let tickerSymbols: string[] = []
  const candleSubscriptions = new Map<string, { symbol: string; resolution: string }>()

  const filterTickerData = (data: Record<string, WsTickerItem>) => {
    if (tickerSymbols.length === 0) return {}

    const allowed = new Set(tickerSymbols)
    const filtered: Record<string, WsTickerItem> = {}

    for (const [symbol, item] of Object.entries(data)) {
      if (allowed.has(symbol) || allowed.has(item.sym)) {
        filtered[symbol] = item
      }
    }

    return filtered
  }

  const base = createBaseSocket<WsOutMessage, (msg: Record<string, WsTickerItem>) => void>(
    (raw, listeners) => {
      try {
        const parsed = JSON.parse(raw) as { code?: string } & Record<string, unknown>
        if (parsed.code === 'TICKER_SNAPSHOT' || parsed.code === 'TICKER_UPDATE') {
          const msg = parsed as unknown as WsTickerServerMessage
          const filtered = filterTickerData(msg.data)
          if (Object.keys(filtered).length > 0) {
            listeners.forEach(fn => fn(filtered))
          }
          return
        }
        if (parsed.code === 'CANDLE_UPDATE') {
          const msg = parsed as unknown as WsCandleUpdateMessage
          candleListeners.forEach(fn => fn(msg))
          return
        }
      } catch {
        // 파싱 실패 시 무시
      }
    },
    undefined,
    {
      prepareUrl: withSocketTicket,
      useAuthProtocols: false,
      getReconnectMessages: () => {
        const messages: WsOutMessage[] = []
        if (tickerSymbols.length > 0) {
          messages.push({ code: 'SUBSCRIBE_TICKER', data: { symbols: tickerSymbols } })
        }
        for (const { symbol, resolution } of candleSubscriptions.values()) {
          messages.push({ code: 'SUBSCRIBE_CANDLE', data: { symbol, resolution } })
        }
        return messages
      },
    },
  )

  const subscribe = (symbols: string[] = []) => {
    tickerSymbols = [...symbols]
    if (tickerSymbols.length === 0) {
      base.send({ code: 'UNSUBSCRIBE_TICKER', data: {} })
      return
    }
    base.send({ code: 'SUBSCRIBE_TICKER', data: { symbols: tickerSymbols } })
  }

  const unsubscribe = () => {
    tickerSymbols = []
    base.send({ code: 'UNSUBSCRIBE_TICKER', data: {} })
  }

  const subscribeCandle = (symbol: string, resolution: string) => {
    candleSubscriptions.set(candleKey(symbol, resolution), { symbol, resolution })
    base.send({ code: 'SUBSCRIBE_CANDLE', data: { symbol, resolution } })
  }

  const unsubscribeCandle = (symbol: string, resolution: string) => {
    candleSubscriptions.delete(candleKey(symbol, resolution))
    base.send({ code: 'UNSUBSCRIBE_CANDLE', data: { symbol, resolution } })
  }

  const addCandleListener = (fn: CandleListener): (() => void) => {
    candleListeners.add(fn)
    return () => {
      candleListeners.delete(fn)
    }
  }

  return {
    ...base,
    subscribe,
    unsubscribe,
    subscribeCandle,
    unsubscribeCandle,
    addCandleListener,
  }
}

export const tickerSocket = createTickerSocket()
